import { useState, useEffect, useRef } from 'react'
import { Search, Users, Building2, Loader2, Plus, X } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { kontakteAPI, type KontaktSearchResult } from '@/utils/api'

interface KontaktAutocompleteProps {
  value: KontaktSearchResult | null
  onChange: (kontakt: KontaktSearchResult | null) => void
  placeholder?: string
  tagFilter?: string // e.g., 'airline' for beklagter
  onCreateNew?: () => void
  disabled?: boolean
  label?: string
}

export function KontaktAutocomplete({
  value,
  onChange,
  placeholder = 'Kontakt suchen...',
  tagFilter,
  onCreateNew,
  disabled,
  label,
}: KontaktAutocompleteProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<KontaktSearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Debounced search
  useEffect(() => {
    if (query.length < 2) {
      setResults([])
      return
    }

    const timer = setTimeout(async () => {
      setLoading(true)
      try {
        const data = await kontakteAPI.searchKontakte(query, {
          tag: tagFilter,
          limit: 10,
        })
        setResults(data)
      } catch (err) {
        console.error('Search failed:', err)
        setResults([])
      } finally {
        setLoading(false)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [query, tagFilter])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = (kontakt: KontaktSearchResult) => {
    onChange(kontakt)
    setQuery('')
    setOpen(false)
  }

  const handleClear = () => {
    onChange(null)
    setQuery('')
  }

  const handleInputFocus = () => {
    setOpen(true)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value)
    setOpen(true)
  }

  // If a value is selected, show it as a "chip"
  if (value) {
    return (
      <div className="space-y-2">
        {label && <label className="text-sm font-medium text-slate-700">{label}</label>}
        <div className="flex items-center gap-2 p-2 bg-slate-50 border border-slate-200 rounded-lg">
          <div className="p-1.5 rounded-md bg-white border border-slate-200">
            {value.kontakt_type === 'person' ? (
              <Users className="h-4 w-4 text-blue-600" />
            ) : (
              <Building2 className="h-4 w-4 text-purple-600" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-slate-900 truncate">{value.display_name}</p>
            {value.iata_code && (
              <span className="text-xs font-mono text-slate-500">{value.iata_code}</span>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClear}
            disabled={disabled}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-2" ref={containerRef}>
      {label && <label className="text-sm font-medium text-slate-700">{label}</label>}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          ref={inputRef}
          value={query}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          disabled={disabled}
          className="pl-10"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 animate-spin" />
        )}

        {/* Dropdown */}
        {open && (query.length >= 2 || onCreateNew) && (
          <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-auto">
            {results.length > 0 ? (
              <>
                {results.map((kontakt) => (
                  <button
                    key={kontakt.id}
                    onClick={() => handleSelect(kontakt)}
                    className="w-full flex items-center gap-3 p-3 hover:bg-slate-50 transition-colors text-left"
                  >
                    <div className="p-1.5 rounded-md bg-slate-100">
                      {kontakt.kontakt_type === 'person' ? (
                        <Users className="h-4 w-4 text-blue-600" />
                      ) : (
                        <Building2 className="h-4 w-4 text-purple-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">
                        {kontakt.display_name}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        {kontakt.iata_code && (
                          <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">
                            {kontakt.iata_code}
                          </span>
                        )}
                        {kontakt.primary_email && (
                          <span className="truncate">{kontakt.primary_email}</span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </>
            ) : query.length >= 2 && !loading ? (
              <div className="p-3 text-center text-sm text-slate-500">
                Keine Ergebnisse gefunden
              </div>
            ) : null}

            {/* Create new option */}
            {onCreateNew && (
              <button
                onClick={() => {
                  setOpen(false)
                  onCreateNew()
                }}
                className="w-full flex items-center gap-3 p-3 border-t border-slate-100 hover:bg-blue-50 transition-colors text-left text-blue-600"
              >
                <div className="p-1.5 rounded-md bg-blue-100">
                  <Plus className="h-4 w-4" />
                </div>
                <span className="font-medium">Neuen Kontakt erstellen</span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
